import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {LOADING_STATES} from '@Libraries/email-marketing-campaigns-library';
import {
  checkEmailVerificationForSenderProfile,
  createSenderProfile,
  deleteSenderProfile,
  fetchDomainAuthenticationDetails,
  deleteDomainAuthentication,
  generateDnsRecords,
  initEmailMarketingCampaignSettings,
  isEmailVerifiedForSenderProfile,
  updateDefaultSenderProfile,
  updateSenderProfile,
} from './email-marketing-campaign-settings-thunk';
import type {EmailMarketingCampaignSettingsState} from './email-marketing-campaign-settings.types';
import {DomainAuthenticationStatus, UNAUTHENTICATED_DOMAIN_DETAILS} from './email-marketing-campaign-settings.types';

export const emailMarketingCampaignSettingsExtraReducers = (builder: ActionReducerMapBuilder<EmailMarketingCampaignSettingsState>): void => {
  builder.addCase(initEmailMarketingCampaignSettings.pending, (state) => {
    state.settingsLoadingState = LOADING_STATES.LOADING;
  });

  builder.addCase(initEmailMarketingCampaignSettings.fulfilled, (state, {payload}) => {
    state.settingsLoadingState = LOADING_STATES.LOADED;
    state.senderProfiles = payload.senderProfiles;
    state.domainAuthenticationDetails = payload.domainAuthenticationDetails;
    state.emailSends = payload.emailSends;
    state.credits = payload.credits;
    state.defaultSenderProfileId = payload.defaultSenderProfileId;
    state.isCustomDomainEnabledForPremiumPlus = payload.isCustomDomainEnabledForPremiumPlus;
    state.accountEmail = payload.accountEmail;
    state.physicalAddress = payload.physicalAddress;
  });

  builder.addCase(fetchDomainAuthenticationDetails.fulfilled, (state, {payload}) => {
    state.domainAuthenticationDetails = payload.domainAuthenticationDetails;
  });

  builder.addCase(generateDnsRecords.fulfilled, (state, {payload}) => {
    state.domainAuthenticationDetails[payload.domain] = {status: DomainAuthenticationStatus.PENDING, dnsRecords: payload.dnsRecords, authenticatedOn: null};
  });

  builder.addCase(deleteDomainAuthentication.fulfilled, (state, {payload}) => {
    state.domainAuthenticationDetails[payload.domain] = UNAUTHENTICATED_DOMAIN_DETAILS;
  });

  builder.addCase(deleteSenderProfile.fulfilled, (state, {payload}) => {
    if (payload.newDefaultSenderProfileId !== undefined) {
      state.senderProfiles[payload.newDefaultSenderProfileId].isDefault = true;
      state.defaultSenderProfileId = payload.newDefaultSenderProfileId;
    }
    state.senderProfiles = Object.fromEntries(Object.entries(state.senderProfiles).filter(([senderProfileId]) => senderProfileId !== String(payload.deletedSenderProfileId)));
  });

  builder.addCase(updateSenderProfile.fulfilled, (state, {payload}) => {
    state.senderProfiles[payload.id] = payload;
  });

  builder.addCase(updateDefaultSenderProfile.fulfilled, (state, {payload}) => {
    const previousDefaultSenderProfile = state.senderProfiles[payload.previousDefaultSenderProfileId];
    if (previousDefaultSenderProfile) {
      previousDefaultSenderProfile.isDefault = false;
    }
    state.senderProfiles[payload.newDefaultSenderProfileId].isDefault = true;
    state.defaultSenderProfileId = payload.newDefaultSenderProfileId;
  });

  builder.addCase(createSenderProfile.fulfilled, (state, {payload}) => {
    state.senderProfiles[payload.senderProfile.id] = payload.senderProfile;
  });

  builder.addCase(isEmailVerifiedForSenderProfile.fulfilled, (state, {payload}) => {
    if (payload.isVerified) {
      state.senderProfiles[payload.idSenderProfile].isVerified = true;
    }
  });

  builder.addCase(checkEmailVerificationForSenderProfile.fulfilled, (state, {payload}) => {
    if (!payload.isAvailable) {
      return;
    }

    state.senderProfiles[payload.data.idSenderProfile].isVerified = true;
    if (!state.defaultSenderProfileId) {
      state.senderProfiles[payload.data.idSenderProfile].isDefault = true;
      state.defaultSenderProfileId = payload.data.idSenderProfile;
    }
  });
};
